/**
 * Rastreamento — o mapa vivo da frota e das ferramentas (26/08, Lucas).
 *
 * Três abas:
 *   MAPA    — onde cada rastreador está agora, com o trajeto do dia a um clique
 *             e a configuração (nome, vínculo, limite de velocidade).
 *   CERCAS  — as cercas virtuais: círculo (clique no centro + raio) ou área
 *             desenhada (cliques nos vértices). Sair de uma cerca gera alerta.
 *   ALERTAS — o histórico do que apitou: velocidade sustentada e fugas de cerca.
 *
 * O mapa é o GOOGLE MAPS (pedido do Lucas, 26/08 — trocou o Leaflet/OSM da
 * primeira versão): o botão Satélite é o que faz diferença em canteiro, para
 * desenhar cerca em cima da obra de verdade. A chave é de navegador, pública
 * por construção e restrita aos nossos domínios — ver comum/google-maps.ts.
 * Quem detecta os alertas é o servidor Traccar; esta tela configura e mostra.
 */

import { api, ErroApi } from '../../api.js';
import { escapar } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';
import { carregarGoogleMaps } from '../../comum/google-maps.js';

interface Rastreador {
  id: number;
  identificador: string;
  nome: string | null;
  ativo: boolean;
  velocidade_max_kmh: number | null;
  veiculo_id: number | null;
  veiculoPlaca: string | null;
  veiculoDescricao: string | null;
  ferramenta_unidade_id: number | null;
  ferramentaPatrimonio: string | null;
  ferramentaNome: string | null;
  posicaoEm: string | null;
  latitude: number | null;
  longitude: number | null;
  velocidadeKmH: number | null;
  ignicao: boolean | null;
  bateria: number | null;
  valido: boolean | null;
}

interface Cerca {
  id: number;
  nome: string;
  tipo: 'circulo' | 'poligono';
  geometria: { lat?: number; lng?: number; raioM?: number; pontos?: [number, number][] };
  ativa: boolean;
}

interface Alerta {
  id: number;
  tipo: string;
  ocorrido_em: string;
  detalhes: {
    velocidadeKmH?: number | null;
    limiteKmH?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    cercaNome?: string | null;
  } | null;
  rastreadorNome: string | null;
  identificador: string;
  veiculoPlaca: string | null;
  cercaNome: string | null;
}

interface Veiculo {
  id: number;
  placa: string;
  descricao: string | null;
}
interface FerramentaUnidade {
  id: number;
  patrimonio: string;
  itemNome: string;
}

/** Goiânia — o centro de tudo enquanto não houver posição melhor. */
const CENTRO_PADRAO = { lat: -16.6869, lng: -49.2648 };
const AZUL = '#164A7F';
const AZUL_ESCURO = '#0F3560';
const DOURADO = '#C9A961';

const dataHoraBr = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

function rotuloDe(r: Rastreador): string {
  if (r.nome) return r.nome;
  if (r.veiculoPlaca) return `${r.veiculoPlaca}${r.veiculoDescricao ? ` — ${r.veiculoDescricao}` : ''}`;
  if (r.ferramentaNome) return `${r.ferramentaNome} (pat. ${r.ferramentaPatrimonio})`;
  return r.identificador;
}

async function novoMapa(el: HTMLElement): Promise<google.maps.Map> {
  const g = await carregarGoogleMaps();
  return new g.Map(el, {
    center: CENTRO_PADRAO,
    zoom: 12,
    // O controle de tipo é o botão Mapa/Satélite — a razão de usar o Google aqui.
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: true,
    clickableIcons: false,
  });
}

function desenharCercaNoMapa(
  mapa: google.maps.Map,
  cerca: Cerca,
): google.maps.Circle | google.maps.Polygon {
  const estilo = { strokeColor: DOURADO, strokeWeight: 2, fillColor: DOURADO, fillOpacity: 0.08 };
  const forma =
    cerca.tipo === 'circulo'
      ? new google.maps.Circle({
          ...estilo,
          center: { lat: cerca.geometria.lat!, lng: cerca.geometria.lng! },
          radius: cerca.geometria.raioM!,
        })
      : new google.maps.Polygon({
          ...estilo,
          paths: cerca.geometria.pontos!.map(([lat, lng]) => ({ lat, lng })),
        });
  forma.setMap(mapa);
  const info = new google.maps.InfoWindow({ content: `<b>${escapar(cerca.nome)}</b>` });
  forma.addListener('click', (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      info.setPosition(e.latLng);
      info.open({ map: mapa });
    }
  });
  return forma;
}

/** Os limites de uma cerca já desenhada — para enquadrar o mapa nelas. */
function estenderLimites(
  limites: google.maps.LatLngBounds,
  forma: google.maps.Circle | google.maps.Polygon,
): void {
  if (forma instanceof google.maps.Circle) {
    const b = forma.getBounds();
    if (b) limites.union(b);
  } else {
    forma.getPath().forEach((p) => limites.extend(p));
  }
}

export function montarRastreamento(casco: HTMLElement): void {
  casco.innerHTML = `
    <nav class="fr-abas">
      <button class="chip chip-ativo" data-ra-aba="mapa">🗺️ Mapa</button>
      <button class="chip" data-ra-aba="cercas">🚧 Cercas virtuais</button>
      <button class="chip" data-ra-aba="alertas">🔔 Alertas</button>
    </nav>
    <div data-ra-corpo></div>`;
  const raiz = casco.querySelector<HTMLElement>('[data-ra-corpo]')!;
  casco.querySelectorAll<HTMLElement>('[data-ra-aba]').forEach((b) => {
    b.addEventListener('click', () => {
      casco.querySelectorAll('[data-ra-aba]').forEach((x) => x.classList.toggle('chip-ativo', x === b));
      const aba = b.dataset['raAba'];
      if (aba === 'cercas') void montarCercas();
      else if (aba === 'alertas') void montarAlertas();
      else void montarMapa();
    });
  });

  // ------------------------------------------------------------------- MAPA

  async function montarMapa(): Promise<void> {
    raiz.innerHTML = `
      <div class="rast-mapa" id="ra-mapa"><p class="carregando">Carregando o mapa…</p></div>
      <section class="ped-painel">
        <h3>🛰️ Rastreadores <span class="sub-inline" id="ra-qtd"></span></h3>
        <div class="rolagem" id="ra-lista"><p class="carregando">Carregando…</p></div>
        <p class="dica">A lista se atualiza sozinha a cada 30 segundos. “Trajeto” desenha o
        caminho de hoje no mapa; “Configurar” dá nome, vínculo e limite de velocidade.
        Use o botão <b>Satélite</b> do mapa para ver as obras de verdade.</p>
      </section>`;
    const alvoMapa = raiz.querySelector<HTMLElement>('#ra-mapa')!;
    let mapa: google.maps.Map;
    try {
      mapa = await novoMapa(alvoMapa);
    } catch (erro) {
      alvoMapa.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'O mapa não carregou.',
      )}</p>`;
      return;
    }
    const janela = new google.maps.InfoWindow();
    const marcadores = new Map<number, google.maps.Marker>();
    let trajeto: google.maps.Polyline | undefined;
    let centralizou = false;

    // As cercas aparecem no mapa principal também — dá contexto ao trajeto.
    void api
      .get<{ cercas: Cerca[] }>('/api/rastreamento/cercas')
      .then((r) => r.cercas.filter((c) => c.ativa).forEach((c) => desenharCercaNoMapa(mapa, c)))
      .catch(() => undefined);

    async function atualizar(): Promise<void> {
      // A tela foi trocada? O laço morre junto com ela.
      if (!raiz.contains(alvoMapa)) return;
      let rastreadores: Rastreador[];
      try {
        rastreadores = (await api.get<{ rastreadores: Rastreador[] }>('/api/rastreamento/atual'))
          .rastreadores;
      } catch (erro) {
        raiz.querySelector('#ra-lista')!.innerHTML = `<p class="aviso">${escapar(
          erro instanceof ErroApi ? erro.message : 'Não consegui carregar os rastreadores.',
        )}</p>`;
        return;
      }
      raiz.querySelector('#ra-qtd')!.textContent = String(rastreadores.length);

      const comPosicao = rastreadores.filter(
        (r) => r.latitude !== null && !(r.latitude === 0 && r.longitude === 0),
      );
      for (const r of comPosicao) {
        const ponto = { lat: r.latitude!, lng: r.longitude! };
        // Posição parada há mais de 2h esmaece — “visto por último”, não “está aqui”.
        const fresca = r.posicaoEm && Date.now() - new Date(r.posicaoEm).getTime() < 2 * 3600_000;
        const cor = fresca ? AZUL : '#8a8f98';
        const icone: google.maps.Symbol = {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: cor,
          fillOpacity: 0.75,
          strokeColor: cor,
          strokeWeight: 2,
        };
        const existente = marcadores.get(r.id);
        const marcador =
          existente ?? new google.maps.Marker({ map: mapa, position: ponto, icon: icone });
        marcador.setPosition(ponto);
        marcador.setIcon(icone);
        marcador.setTitle(rotuloDe(r));
        google.maps.event.clearListeners(marcador, 'click');
        marcador.addListener('click', () => {
          janela.setContent(
            `<b>${escapar(rotuloDe(r))}</b><br>` +
              `${dataHoraBr(r.posicaoEm)} — ${r.velocidadeKmH ?? 0} km/h<br>` +
              `${r.ignicao === null ? '' : r.ignicao ? '🔑 ignição ligada<br>' : '🔒 ignição desligada<br>'}` +
              `${r.bateria !== null ? `🔋 ${r.bateria}` : ''}`,
          );
          janela.open({ map: mapa, anchor: marcador });
        });
        marcadores.set(r.id, marcador);
      }
      if (!centralizou && comPosicao.length > 0) {
        centralizou = true;
        const limites = new google.maps.LatLngBounds();
        comPosicao.forEach((r) => limites.extend({ lat: r.latitude!, lng: r.longitude! }));
        mapa.fitBounds(limites, 40);
        if (comPosicao.length === 1) mapa.setZoom(15);
      }

      raiz.querySelector('#ra-lista')!.innerHTML =
        rastreadores.length === 0
          ? `<p class="dica">Nenhum rastreador ainda — ao ligar um aparelho apontado para o
             servidor, ele aparece aqui sozinho.</p>`
          : `<table class="tabela"><thead><tr>
               <th>Rastreador</th><th>Vínculo</th><th>Última posição</th>
               <th class="num">Velocidade</th><th class="num">Limite</th><th class="acoes"></th>
             </tr></thead><tbody>
             ${rastreadores
               .map(
                 (r) => `<tr>
                 <td class="cod">${escapar(rotuloDe(r))}${r.ativo ? '' : ' <span class="zero">(inativo)</span>'}</td>
                 <td>${
                   r.veiculoPlaca
                     ? `🚛 ${escapar(r.veiculoPlaca)}`
                     : r.ferramentaNome
                       ? `🧰 ${escapar(r.ferramentaNome)}`
                       : '<span class="zero">— nenhum —</span>'
                 }</td>
                 <td class="data">${dataHoraBr(r.posicaoEm)}${r.valido === false ? ' <span title="sem fix de GPS">📡</span>' : ''}</td>
                 <td class="num">${r.velocidadeKmH !== null ? `${r.velocidadeKmH} km/h` : '—'}</td>
                 <td class="num">${r.velocidade_max_kmh !== null ? `${r.velocidade_max_kmh} km/h` : '—'}</td>
                 <td class="acoes">
                   <button class="btn btn-claro" data-centrar="${r.id}" ${r.latitude === null ? 'disabled' : ''}>📍</button>
                   <button class="btn btn-claro" data-trajeto="${r.id}">〰️ Trajeto</button>
                   <button class="btn btn-claro" data-config="${r.id}">⚙️</button>
                 </td>
               </tr>`,
               )
               .join('')}
             </tbody></table>`;

      raiz.querySelectorAll<HTMLElement>('[data-centrar]').forEach((b) =>
        b.addEventListener('click', () => {
          const m = marcadores.get(Number(b.dataset['centrar']));
          const posicao = m?.getPosition();
          if (m && posicao) {
            mapa.setCenter(posicao);
            mapa.setZoom(16);
            google.maps.event.trigger(m, 'click');
          }
        }),
      );
      raiz.querySelectorAll<HTMLElement>('[data-trajeto]').forEach((b) =>
        b.addEventListener('click', () => void desenharTrajeto(Number(b.dataset['trajeto']))),
      );
      raiz.querySelectorAll<HTMLElement>('[data-config]').forEach((b) =>
        b.addEventListener('click', () => {
          const r = rastreadores.find((x) => x.id === Number(b.dataset['config']));
          if (r) void abrirConfiguracao(r);
        }),
      );
    }

    async function desenharTrajeto(id: number): Promise<void> {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      try {
        const { pontos } = await api.get<{
          pontos: { latitude: number; longitude: number; valido: boolean }[];
        }>(`/api/rastreamento/${id}/historico?de=${hoje.toISOString()}`);
        const validos = pontos.filter((p) => p.valido && !(p.latitude === 0 && p.longitude === 0));
        if (validos.length < 2) {
          avisar('Ainda não há trajeto registrado hoje para este rastreador.');
          return;
        }
        trajeto?.setMap(null);
        trajeto = new google.maps.Polyline({
          map: mapa,
          path: validos.map((p) => ({ lat: p.latitude, lng: p.longitude })),
          strokeColor: AZUL_ESCURO,
          strokeWeight: 3,
          strokeOpacity: 0.85,
        });
        const limites = new google.maps.LatLngBounds();
        validos.forEach((p) => limites.extend({ lat: p.latitude, lng: p.longitude }));
        mapa.fitBounds(limites, 40);
      } catch (e) {
        avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui buscar o trajeto.');
      }
    }

    await atualizar();
    const laco = setInterval(() => {
      if (!raiz.contains(alvoMapa)) {
        clearInterval(laco);
        return;
      }
      void atualizar();
    }, 30_000);
  }

  // A CONFIGURAÇÃO troca a área pela ficha, como a Frota faz com o veículo.
  async function abrirConfiguracao(r: Rastreador): Promise<void> {
    let veiculos: Veiculo[] = [];
    let ferramentas: FerramentaUnidade[] = [];
    try {
      const [rv, rf] = await Promise.all([
        api.get<{ veiculos: Veiculo[] }>('/api/frota/veiculos'),
        api.get<{ unidades: FerramentaUnidade[] }>('/api/ferramentas'),
      ]);
      veiculos = rv.veiculos;
      ferramentas = rf.unidades;
    } catch {
      // Sem as listas dá para configurar o resto — os selects ficam vazios.
    }
    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>⚙️ ${escapar(rotuloDe(r))} <span class="sub-inline">${escapar(r.identificador)}</span></h3>
        <div class="form-2col">
          <label class="rotulado"><span>Nome de exibição</span>
            <input id="ra-nome" maxlength="200" value="${escapar(r.nome ?? '')}" placeholder="Caminhão baú / Betoneira 2"></label>
          <label class="rotulado"><span>Velocidade máxima (km/h) — vazio desliga o alerta</span>
            <input id="ra-vel" type="number" min="10" max="200" inputmode="numeric" value="${r.velocidade_max_kmh ?? ''}"></label>
          <label class="rotulado"><span>Vínculo: veículo</span>
            <select id="ra-veiculo"><option value="">— nenhum —</option>
              ${veiculos.map((v) => `<option value="${v.id}" ${v.id === r.veiculo_id ? 'selected' : ''}>${escapar(v.placa)}${v.descricao ? ` — ${escapar(v.descricao)}` : ''}</option>`).join('')}
            </select></label>
          <label class="rotulado"><span>Vínculo: ferramenta (antifurto)</span>
            <select id="ra-ferramenta"><option value="">— nenhuma —</option>
              ${ferramentas.map((f) => `<option value="${f.id}" ${f.id === r.ferramenta_unidade_id ? 'selected' : ''}>${escapar(f.itemNome)} (pat. ${escapar(f.patrimonio)})</option>`).join('')}
            </select></label>
        </div>
        <p class="dica">O alerta dispara quando o aparelho fica mais de <b>30 segundos</b> acima
        do limite. Vincule a UM veículo OU a UMA ferramenta — escolher um limpa o outro.</p>
        <div class="conf-acoes">
          <button class="btn" id="ra-salvar">✓ Salvar</button>
          <button class="btn btn-claro" id="ra-voltar">Voltar ao mapa</button>
        </div>
      </section>`;
    raiz.querySelector('#ra-voltar')!.addEventListener('click', () => void montarMapa());
    raiz.querySelector('#ra-salvar')!.addEventListener('click', () => {
      const veiculo = raiz.querySelector<HTMLSelectElement>('#ra-veiculo')!.value;
      const ferramenta = raiz.querySelector<HTMLSelectElement>('#ra-ferramenta')!.value;
      if (veiculo && ferramenta && Number(veiculo) !== r.veiculo_id && Number(ferramenta) !== r.ferramenta_unidade_id) {
        return avisarBloqueante('Escolha veículo OU ferramenta, não os dois.');
      }
      const vel = raiz.querySelector<HTMLInputElement>('#ra-vel')!.value.trim();
      void api
        .put<{ ok: boolean; avisoTraccar?: string }>(`/api/rastreamento/${r.id}`, {
          nome: raiz.querySelector<HTMLInputElement>('#ra-nome')!.value.trim() || null,
          velocidadeMaxKmh: vel === '' ? null : Number(vel),
          veiculoId: veiculo ? Number(veiculo) : null,
          ferramentaUnidadeId: ferramenta ? Number(ferramenta) : null,
        })
        .then((resp) => {
          if (resp.avisoTraccar) avisarBloqueante(resp.avisoTraccar);
          else avisar('Rastreador atualizado.');
          void montarMapa();
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui salvar.'));
    });
  }

  // ----------------------------------------------------------------- CERCAS

  async function montarCercas(): Promise<void> {
    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🚧 Cercas virtuais</h3>
        <p class="dica">Sair de uma cerca gera alerta (app e, quando ligado, WhatsApp).
        <b>Círculo:</b> toque em “Novo círculo”, clique no centro no mapa e ajuste o raio.
        <b>Área:</b> toque em “Nova área”, clique nos cantos da região e feche. Para cobrir
        uma cidade inteira, desenhe um círculo ou área em volta dela. No <b>Satélite</b>
        dá para desenhar a cerca em cima da obra de verdade.</p>
        <div class="conf-acoes">
          <button class="btn btn-claro" id="ce-circulo">⭕ Novo círculo</button>
          <button class="btn btn-claro" id="ce-poligono">✏️ Nova área</button>
          <span id="ce-instrucao" class="dica"></span>
        </div>
      </section>
      <div class="rast-mapa" id="ce-mapa"><p class="carregando">Carregando o mapa…</p></div>
      <section class="ped-painel">
        <div id="ce-form" style="display:none">
          <div class="form-2col">
            <label class="rotulado"><span>Nome da cerca</span><input id="ce-nome" maxlength="120" placeholder="Obra Vila Galvão / Região de Goiânia"></label>
            <label class="rotulado" id="ce-raio-caixa"><span>Raio (metros)</span><input id="ce-raio" type="number" min="50" max="500000" value="1000" inputmode="numeric"></label>
          </div>
          <div class="conf-acoes">
            <button class="btn" id="ce-salvar">✓ Salvar cerca</button>
            <button class="btn btn-claro" id="ce-cancelar">Cancelar</button>
          </div>
        </div>
        <div id="ce-lista"><p class="carregando">Carregando…</p></div>
      </section>`;

    const alvoMapa = raiz.querySelector<HTMLElement>('#ce-mapa')!;
    let mapa: google.maps.Map;
    try {
      mapa = await novoMapa(alvoMapa);
    } catch (erro) {
      alvoMapa.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'O mapa não carregou.',
      )}</p>`;
      return;
    }
    const camadas = new Map<number, google.maps.Circle | google.maps.Polygon>();
    let modo: 'circulo' | 'poligono' | null = null;
    let centro: google.maps.LatLngLiteral | null = null;
    let vertices: google.maps.LatLngLiteral[] = [];
    let previa: google.maps.Circle | google.maps.Polygon | google.maps.Polyline | null = null;

    const instrucao = raiz.querySelector<HTMLElement>('#ce-instrucao')!;
    const form = raiz.querySelector<HTMLElement>('#ce-form')!;
    const raioCaixa = raiz.querySelector<HTMLElement>('#ce-raio-caixa')!;
    const campoRaio = raiz.querySelector<HTMLInputElement>('#ce-raio')!;

    function limparRascunho(): void {
      previa?.setMap(null);
      previa = null;
      centro = null;
      vertices = [];
      modo = null;
      form.style.display = 'none';
      instrucao.textContent = '';
    }

    function redesenharPrevia(): void {
      previa?.setMap(null);
      previa = null;
      const rascunho = {
        strokeColor: AZUL,
        strokeWeight: 2,
        fillColor: AZUL,
        fillOpacity: 0.1,
        clickable: false,
      };
      if (modo === 'circulo' && centro) {
        previa = new google.maps.Circle({
          ...rascunho,
          map: mapa,
          center: centro,
          radius: Number(campoRaio.value) || 1000,
        });
      } else if (modo === 'poligono' && vertices.length > 0) {
        previa =
          vertices.length < 3
            ? new google.maps.Polyline({ map: mapa, path: vertices, strokeColor: AZUL, clickable: false })
            : new google.maps.Polygon({ ...rascunho, map: mapa, paths: vertices });
      }
    }

    raiz.querySelector('#ce-circulo')!.addEventListener('click', () => {
      limparRascunho();
      modo = 'circulo';
      raioCaixa.style.display = '';
      instrucao.textContent = 'Clique no mapa onde é o CENTRO do círculo.';
    });
    raiz.querySelector('#ce-poligono')!.addEventListener('click', () => {
      limparRascunho();
      modo = 'poligono';
      raioCaixa.style.display = 'none';
      instrucao.textContent = 'Clique nos CANTOS da área (3 ou mais) e depois salve.';
    });
    raiz.querySelector('#ce-cancelar')!.addEventListener('click', limparRascunho);
    campoRaio.addEventListener('input', redesenharPrevia);

    mapa.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const ponto = e.latLng.toJSON();
      if (modo === 'circulo') {
        centro = ponto;
        form.style.display = '';
        instrucao.textContent = 'Ajuste o raio e dê um nome.';
      } else if (modo === 'poligono') {
        vertices.push(ponto);
        if (vertices.length >= 3) form.style.display = '';
      } else {
        return;
      }
      redesenharPrevia();
    });

    raiz.querySelector('#ce-salvar')!.addEventListener('click', () => {
      const nome = raiz.querySelector<HTMLInputElement>('#ce-nome')!.value.trim();
      if (!nome) return avisarBloqueante('Dê um nome à cerca.');
      const corpo =
        modo === 'circulo'
          ? centro && {
              tipo: 'circulo',
              nome,
              geometria: { lat: centro.lat, lng: centro.lng, raioM: Number(campoRaio.value) || 1000 },
            }
          : vertices.length >= 3 && {
              tipo: 'poligono',
              nome,
              geometria: { pontos: vertices.map((v) => [v.lat, v.lng]) },
            };
      if (!corpo) return avisarBloqueante('Desenhe a cerca no mapa primeiro.');
      void api
        .post<{ cerca: Cerca }>('/api/rastreamento/cercas', corpo)
        .then(() => {
          avisar(`Cerca "${nome}" criada — já está vigiando.`);
          limparRascunho();
          void listarCercas();
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui criar a cerca.'));
    });

    async function listarCercas(): Promise<void> {
      let cercas: Cerca[];
      try {
        cercas = (await api.get<{ cercas: Cerca[] }>('/api/rastreamento/cercas')).cercas;
      } catch (erro) {
        raiz.querySelector('#ce-lista')!.innerHTML = `<p class="aviso">${escapar(
          erro instanceof ErroApi ? erro.message : 'Não consegui carregar as cercas.',
        )}</p>`;
        return;
      }
      camadas.forEach((c) => c.setMap(null));
      camadas.clear();
      const ativas = cercas.filter((c) => c.ativa);
      ativas.forEach((c) => camadas.set(c.id, desenharCercaNoMapa(mapa, c)));
      if (ativas.length > 0) {
        const limites = new google.maps.LatLngBounds();
        camadas.forEach((forma) => estenderLimites(limites, forma));
        mapa.fitBounds(limites, 40);
      }
      raiz.querySelector('#ce-lista')!.innerHTML =
        cercas.length === 0
          ? `<p class="dica">Nenhuma cerca ainda.</p>`
          : `<table class="tabela"><thead><tr><th>Nome</th><th>Tipo</th><th class="acoes"></th></tr></thead>
             <tbody>${cercas
               .map(
                 (c) => `<tr>
                   <td class="cod">${escapar(c.nome)}</td>
                   <td>${c.tipo === 'circulo' ? '⭕ Círculo' : '✏️ Área'}</td>
                   <td class="acoes"><button class="btn btn-claro" data-apagar="${c.id}">🗑️ Excluir</button></td>
                 </tr>`,
               )
               .join('')}</tbody></table>`;
      raiz.querySelectorAll<HTMLElement>('[data-apagar]').forEach((b) =>
        b.addEventListener('click', () => {
          if (!confirm('Excluir esta cerca? Ela para de vigiar na hora.')) return;
          void api
            .del(`/api/rastreamento/cercas/${b.dataset['apagar']}`)
            .then(() => {
              avisar('Cerca excluída.');
              void listarCercas();
            })
            .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui excluir.'));
        }),
      );
    }

    await listarCercas();
  }

  // ---------------------------------------------------------------- ALERTAS

  async function montarAlertas(): Promise<void> {
    raiz.innerHTML = `<section class="ped-painel">
      <h3>🔔 Alertas <span class="sub-inline" id="al-qtd"></span></h3>
      <div class="rolagem" id="al-lista"><p class="carregando">Carregando…</p></div>
      <p class="dica">Velocidade: mais de 30 segundos acima do limite do aparelho.
      Cerca: o rastreador saiu de uma cerca ativa. O aviso chega pelo sino/push — e pelo
      WhatsApp quando a central de Avisos tiver números configurados.</p>
    </section>`;
    let alertas: Alerta[];
    try {
      alertas = (await api.get<{ alertas: Alerta[] }>('/api/rastreamento/alertas')).alertas;
    } catch (erro) {
      raiz.querySelector('#al-lista')!.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar os alertas.',
      )}</p>`;
      return;
    }
    raiz.querySelector('#al-qtd')!.textContent = String(alertas.length);
    raiz.querySelector('#al-lista')!.innerHTML =
      alertas.length === 0
        ? `<p class="dica">Nenhum alerta até agora — bom sinal.</p>`
        : `<table class="tabela"><thead><tr>
             <th>Quando</th><th>O quê</th><th>Rastreador</th><th>Detalhe</th><th></th>
           </tr></thead><tbody>
           ${alertas
             .map((a) => {
               const nome = a.rastreadorNome ?? a.veiculoPlaca ?? a.identificador;
               const detalhe =
                 a.tipo === 'velocidade'
                   ? `${a.detalhes?.velocidadeKmH ?? '?'} km/h (limite ${a.detalhes?.limiteKmH ?? '?'})`
                   : `cerca "${a.cercaNome ?? a.detalhes?.cercaNome ?? '?'}"`;
               const rotulo =
                 a.tipo === 'velocidade' ? '🚨 Velocidade'
                 : a.tipo === 'cerca_saida' ? '🚧 Saiu da cerca'
                 : '↩️ Entrou na cerca';
               const mapaLink =
                 a.detalhes?.latitude != null
                   ? `<a class="btn btn-claro" target="_blank" rel="noopener"
                        href="https://www.google.com/maps?q=${a.detalhes.latitude},${a.detalhes.longitude}">📍 Ver no mapa</a>`
                   : '';
               return `<tr>
                 <td class="data">${dataHoraBr(a.ocorrido_em)}</td>
                 <td>${rotulo}</td>
                 <td class="cod">${escapar(nome)}</td>
                 <td>${escapar(detalhe)}</td>
                 <td class="acoes">${mapaLink}</td>
               </tr>`;
             })
             .join('')}
           </tbody></table>`;
  }

  void montarMapa();
}
